import React, { createContext, useState, useEffect, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("elitecare_user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [accessToken, setAccessToken] = useState(() => {
    return localStorage.getItem("elitecare_access_token") || null;
  });

  const [refreshToken, setRefreshToken] = useState(() => {
    return localStorage.getItem("elitecare_refresh_token") || null;
  });

  const [loading, setLoading] = useState(true);

  const logout = React.useCallback(async () => {
    if (accessToken) {
      try {
        await fetch("https://api.elitecarephysiorehab.com/api/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (err) {
        console.error("Logout API call failed:", err);
      }
    }

    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);

    localStorage.removeItem("elitecare_user");
    localStorage.removeItem("elitecare_access_token");
    localStorage.removeItem("elitecare_refresh_token");
  }, [accessToken]);

  const refreshAccessToken = React.useCallback(async () => {
    if (!refreshToken) return null;

    try {
      const response = await fetch("https://api.elitecarephysiorehab.com/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        setAccessToken(data.accessToken);
        localStorage.setItem("elitecare_access_token", data.accessToken);
        return data.accessToken;
      } else {
        // Refresh token expired or invalid, log out
        logout();
        return null;
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return null;
    }
  }, [refreshToken, logout]);

  useEffect(() => {
    // Validate or refresh tokens on initial load
    const initAuth = async () => {
      if (refreshToken && !accessToken) {
        await refreshAccessToken();
      }
      setLoading(false);
    };
    initAuth();
  }, [accessToken, refreshToken, refreshAccessToken]);

  const login = async (email, password) => {
    const response = await fetch("https://api.elitecarephysiorehab.com/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Login failed");
    }

    setUser(data.user);
    setAccessToken(data.accessToken);
    setRefreshToken(data.refreshToken);

    localStorage.setItem("elitecare_user", JSON.stringify(data.user));
    localStorage.setItem("elitecare_access_token", data.accessToken);
    localStorage.setItem("elitecare_refresh_token", data.refreshToken);

    return data.user;
  };
  // Helper function to make authenticated API requests
  const authFetch = async (url, options = {}) => {
    let currentToken = accessToken;

    // Optional: client-side jwt expiry check (simplified version)
    if (currentToken) {
      try {
        const payload = JSON.parse(atob(currentToken.split(".")[1]));
        const expiry = payload.exp * 1000;
        if (Date.now() >= expiry - 5000) {
          // Token is about to expire or has expired, refresh it
          currentToken = await refreshAccessToken();
        }
      } catch (e) {
        console.error("Error parsing JWT:", e);
      }
    }

    if (!currentToken && refreshToken) {
      currentToken = await refreshAccessToken();
    }

    const headers = {
      ...options.headers,
      "Content-Type": "application/json",
    };

    if (currentToken) {
      headers["Authorization"] = `Bearer ${currentToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If unauthorized, attempt to refresh and try again
    if (response.status === 401 || response.status === 403) {
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) {
        headers["Authorization"] = `Bearer ${refreshedToken}`;
        return fetch(url, { ...options, headers });
      }
    }

    return response;
  };

  const value = {
    user,
    accessToken,
    loading,
    login,
    logout,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
